import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import { Spinner } from '@/components/ui/spinner';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { PublicLayout } from '@/layouts/PublicLayout';
import { HomePage } from '@/pages/public/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LegalPage } from '@/pages/public/LegalPage';
import { ProductListPage } from '@/pages/public/ProductListPage';
import { ProductDetailPage } from '@/pages/public/ProductDetailPage';

const OrderPage = lazy(async () => ({ default: (await import('@/pages/public/OrderPage')).OrderPage }));
const LoginPage = lazy(async () => ({ default: (await import('@/pages/admin/LoginPage')).LoginPage }));
const AdminLayout = lazy(async () => ({ default: (await import('@/layouts/AdminLayout')).AdminLayout }));
const DashboardPage = lazy(async () => ({ default: (await import('@/pages/admin/DashboardPage')).DashboardPage }));
const OrdersPage = lazy(async () => ({ default: (await import('@/pages/admin/OrdersPage')).OrdersPage }));
const OrderDetailPage = lazy(async () => ({ default: (await import('@/pages/admin/OrderDetailPage')).OrderDetailPage }));
const OrderIntakePage = lazy(async () => ({ default: (await import('@/pages/admin/OrderIntakePage')).OrderIntakePage }));
const MaintenancePage = lazy(async () => ({ default: (await import('@/pages/admin/MaintenancePage')).MaintenancePage }));
const DeliveriesPage = lazy(async () => ({ default: (await import('@/pages/admin/DeliveriesPage')).DeliveriesPage }));
const DriversPage = lazy(async () => ({ default: (await import('@/pages/admin/DriversPage')).DriversPage }));
const CalendarPage = lazy(async () => ({ default: (await import('@/pages/admin/CalendarPage')).CalendarPage }));
const CustomersPage = lazy(async () => ({ default: (await import('@/pages/admin/CustomersPage')).CustomersPage }));
const CustomerDetailPage = lazy(async () => ({ default: (await import('@/pages/admin/CustomerDetailPage')).CustomerDetailPage }));
const CustomerEditPage = lazy(async () => ({ default: (await import('@/pages/admin/CustomerEditPage')).CustomerEditPage }));
const EquipmentPage = lazy(async () => ({ default: (await import('@/pages/admin/EquipmentPage')).EquipmentPage }));
const EquipmentDetailPage = lazy(async () => ({ default: (await import('@/pages/admin/EquipmentDetailPage')).EquipmentDetailPage }));
const InventoryPage = lazy(async () => ({ default: (await import('@/pages/admin/InventoryPage')).InventoryPage }));
const PairingsPage = lazy(async () => ({ default: (await import('@/pages/admin/PairingsPage')).PairingsPage }));
const EquipmentEditPage = lazy(async () => ({ default: (await import('@/pages/admin/EquipmentEditPage')).EquipmentEditPage }));
const ProductsPage = lazy(async () => ({ default: (await import('@/pages/admin/ProductsPage')).ProductsPage }));
const ProductEditPage = lazy(async () => ({ default: (await import('@/pages/admin/ProductEditPage')).ProductEditPage }));
const FaqPage = lazy(async () => ({ default: (await import('@/pages/admin/FaqPage')).FaqPage }));
const SettingsPage = lazy(async () => ({ default: (await import('@/pages/admin/SettingsPage')).SettingsPage }));
const DeliverySettingsPage = lazy(async () => ({ default: (await import('@/pages/admin/DeliverySettingsPage')).DeliverySettingsPage }));
const UsersPage = lazy(async () => ({ default: (await import('@/pages/admin/UsersPage')).UsersPage }));
const ChangePasswordPage = lazy(async () => ({ default: (await import('@/pages/ChangePasswordPage')).ChangePasswordPage }));
const DriverLayout = lazy(async () => ({ default: (await import('@/layouts/DriverLayout')).DriverLayout }));
const DriverHomePage = lazy(async () => ({ default: (await import('@/pages/driver/DriverHomePage')).DriverHomePage }));

export const AppRoutes = () => (
  <Suspense fallback={<Spinner />}>
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="/termekek" element={<ProductListPage />} />
        <Route path="/termekek/:slug" element={<ProductDetailPage />} />
        <Route path="/rendeles" element={<OrderPage />} />
        <Route path="/aszf" element={<LegalPage />} />
        <Route path="/adatvedelem" element={<LegalPage />} />
        <Route path="/cookie" element={<LegalPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="/admin/belepes" element={<LoginPage />} />

      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="rendelesek" element={<OrdersPage />} />
        <Route path="rendelesek/uj" element={<OrderIntakePage />} />
        <Route path="rendelesek/:id" element={<OrderDetailPage />} />
        <Route path="naptar" element={<CalendarPage />} />
        <Route path="fuvarok" element={<DeliveriesPage />} />
        <Route path="fuvarok/soforok" element={<DriversPage />} />
        <Route path="ugyfelek" element={<CustomersPage />} />
        <Route path="ugyfelek/uj" element={<CustomerEditPage />} />
        <Route path="ugyfelek/:id" element={<CustomerDetailPage />} />
        <Route path="ugyfelek/:id/szerkesztes" element={<CustomerEditPage />} />
        <Route path="eszkozok" element={<EquipmentPage />} />
        <Route path="eszkozok/uj" element={<EquipmentEditPage />} />
        <Route path="eszkozok/parositasok" element={<PairingsPage />} />
        <Route path="keszlet" element={<InventoryPage />} />
        <Route path="karbantartas" element={<MaintenancePage />} />
        <Route path="eszkozok/:id" element={<EquipmentDetailPage />} />
        <Route path="eszkozok/:id/szerkesztes" element={<EquipmentEditPage />} />
        <Route path="termekek" element={<ProductsPage />} />
        <Route path="termekek/uj" element={<ProductEditPage />} />
        <Route path="termekek/:id" element={<ProductEditPage />} />
        <Route path="gyik" element={<FaqPage />} />
        <Route path="beallitasok" element={<SettingsPage />} />
        <Route path="beallitasok/szallitas" element={<DeliverySettingsPage />} />
        <Route path="felhasznalok" element={<UsersPage />} />
        <Route path="jelszo" element={<ChangePasswordPage />} />
      </Route>

      <Route
        path="/sofor"
        element={
          <ProtectedRoute roles={['DRIVER', 'ADMIN']}>
            <DriverLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DriverHomePage />} />
        <Route path="jelszo" element={<ChangePasswordPage />} />
      </Route>
    </Routes>
  </Suspense>
);
